import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import classNames from 'classnames'
import { CogIcon } from '@okcredit/web-lib'
import { IAppState } from 'infra/AppState'
import { ICustomer, ISupplier } from 'state/dashboard/dashboard.types'
import {
  broadcastEvent,
  editEntity,
  hideTxnAlertNudge,
  setPendingTxnAlertUpgrade
} from 'state/dashboard/dashboard.actions'
import { Switch } from 'components/Switch'
import { THEME } from 'components/theme'
import { newPaths } from 'routes'
import { ENTITY_TYPE, ILanguageEntity, LANGUAGES } from 'shared/constants'
import { ReactComponent as SmsSvg } from 'assets/icons/sms-outline.svg'
import { ReactComponent as LanguageSvg } from 'assets/icons/language.svg'
import { ReactComponent as ArrowSvg } from 'assets/icons/arrow-down.svg'
import { ReactComponent as TickSvg } from 'assets/icons/tick.svg'
import { buildTransactionSmsSample } from './smsSample'
import { useTxnAlertBenefit } from './useTxnAlertBenefit'
import { TxnAlertChannel, TxnAlertChannelSheet } from './TxnAlertChannelSheet'
import { AnchoredSheet, findSheetAnchor } from './AnchoredSheet'
import { TransactionAlertSettingsStyles } from './TransactionAlertSettings.styles'

// Android's ic_notifications_none (outline bell) for the registered-user strip.
const BellIcon: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z" />
  </svg>
)

// react-switch needs plain hex (it interpolates colours while dragging). Fixed in both
// themes, matching the iOS toggle: primary green track on, grey track off, white thumb.
const SWITCH_ON = '#1c873b'
const SWITCH_OFF = '#9e9e9e'

// Inline so it wins over the global dark-mode rule that lightens baked #212121 svg fills.
const ICON_TINT = { fill: THEME.brandGreen }

const shortLanguageLabel = (lang: ILanguageEntity) => lang.label.replace(/\(.*\)/, '').trim()

export interface TransactionAlertSettingsProps {
  entityId: string
  entityType: ENTITY_TYPE
  className?: string
  // Where the settings were opened from (analytics)
  source?: 'profile_drawer' | 'add_transaction_nudge'
}

// The iOS/Android "Transaction Alert" settings body: a Language card (drives SMS, WhatsApp
// for this account; voice is not on web yet) and a Transaction Alert card with the OFF/ON toggle, its
// description and a phone-frame preview of the SMS the customer would get. Edits go
// through editEntity, which the dashboard watcher debounces into the PATCH (same path as
// the profile drawer's other fields).
export const TransactionAlertSettings: React.FC<TransactionAlertSettingsProps> = ({
  entityId,
  entityType,
  className,
  source = 'profile_drawer'
}) => {
  const classes = TransactionAlertSettingsStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const isCustomer = entityType === ENTITY_TYPE.CUSTOMER
  const entity = useSelector(
    (state: IAppState) =>
      state.Dashboard[isCustomer ? 'customers' : 'suppliers'].byIds[entityId] as
        | ICustomer
        | ISupplier
        | undefined
  )
  const { loaded, hasAlertBenefit, isOnSmsPremium, whatsappOffered } = useTxnAlertBenefit()
  const [languageOpen, setLanguageOpen] = useState(false)
  const [showUpgrade, setShowUpgrade] = useState(false)
  // SMS-or-WhatsApp sheet for an SMS-Premium merchant while a WhatsApp plan is on offer
  const [channelSheetOpen, setChannelSheetOpen] = useState(false)
  const [pickedChannel, setPickedChannel] = useState<TxnAlertChannel | null>(null)
  // Sheets open as bottom sheets inside the drawer / dialog this sits in (iOS parity)
  const rootRef = useRef<HTMLDivElement>(null)
  const [sheetAnchor, setSheetAnchor] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setSheetAnchor(findSheetAnchor(rootRef.current))
  }, [])

  // Web-only analytics (mobile fires nothing on this screen): every event carries where the
  // settings were opened from, the relation and the account. Declared before the early return
  // so the hook order is stable.
  const track = (event: string, props: Record<string, unknown> = {}) =>
    dispatch(
      broadcastEvent(event, {
        source,
        relation: isCustomer ? 'customer' : 'supplier',
        account_id: entityId,
        ...props
      })
    )
  useEffect(() => {
    if (entity) {
      track('txn_alert_settings_viewed', {
        alert_enabled: Boolean(entity.edited?.values?.txnAlertEnabled),
        registered: Boolean(entity.isRegistered)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId])

  if (!entity) {
    return null
  }

  const { values } = entity.edited
  const alertEnabled = Boolean(values.txnAlertEnabled)
  const lang: ILanguageEntity = values.lang || LANGUAGES[0]
  const name = isCustomer ? (entity as ICustomer).description : (entity as ISupplier).name
  const relation = t(isCustomer ? 'dashboard:RelationCustomer' : 'dashboard:RelationSupplier')
  const sample = buildTransactionSmsSample({ isSupplier: !isCustomer, langKey: lang.key, name })

  const enableAlert = (enabled: boolean) =>
    dispatch(editEntity({ key: 'txnAlertEnabled', entityId, entityType, value: enabled }))

  const onToggle = (enabled: boolean) => {
    // OFF persists at once. ON needs an alert benefit: without it the switch stays off and
    // the upgrade sheet opens instead (nothing is saved). Until the plan is known, ignore.
    if (enabled && !loaded) {
      track('txn_alert_toggle_clicked', { enabled, outcome: 'ignored_plan_loading' })
      return
    }
    if (enabled && !hasAlertBenefit) {
      track('txn_alert_toggle_clicked', { enabled, outcome: 'upgrade_sheet' })
      track('txn_alert_upgrade_sheet_viewed', { whatsapp_offered: whatsappOffered })
      setShowUpgrade(true)
      return
    }
    // On SMS Premium with WhatsApp on offer, switching on first asks which channel (KMP
    // TransactionAlertSettingsRoute.askChannel): the alert stays off until they pick SMS or
    // a WhatsApp purchase lands.
    if (enabled && whatsappOffered && isOnSmsPremium) {
      track('txn_alert_toggle_clicked', { enabled, outcome: 'channel_sheet' })
      track('txn_alert_channel_sheet_viewed')
      setPickedChannel(null)
      setChannelSheetOpen(true)
      return
    }
    track('txn_alert_toggle_clicked', { enabled, outcome: 'saved' })
    enableAlert(enabled)
  }

  // `channel` = already chosen in the sheet here, so My Plan must not ask SMS/WhatsApp again.
  const goToMyPlanPicker = (channel?: TxnAlertChannel) => {
    // Leaving for My Plan: the post-transaction nudge dialog (if this sits in it) must not
    // stay layered over the plan page. It is NOT marked seen — a paid plan marks it via the
    // pending upgrade; an abandoned purchase lets the nudge come back next time (iOS: back
    // from My Plan without buying leaves the settings screen open and unseen).
    dispatch(hideTxnAlertNudge())
    history.push(`${newPaths.myPlan}?source=txn_alert${channel ? `&channel=${channel}` : ''}`)
  }

  const onChannelProceed = () => {
    if (pickedChannel) {
      track('txn_alert_channel_selected', { channel: pickedChannel })
    }
    if (pickedChannel === 'sms') {
      // Staying on SMS: the plan already sends it, so close the sheet and switch the alert on.
      setChannelSheetOpen(false)
      enableAlert(true)
    } else if (pickedChannel === 'whatsapp') {
      // WhatsApp is already the answer, so My Plan skips its SMS/WhatsApp picker and opens
      // straight on the WhatsApp plan's purchase confirmation; the alert stays off until that
      // purchase lands (then the pending upgrade switches it on).
      setChannelSheetOpen(false)
      dispatch(setPendingTxnAlertUpgrade({ entityId, entityType, awaited: 'whatsapp' }))
      goToMyPlanPicker('whatsapp')
    }
  }

  const onPickLanguage = (picked: ILanguageEntity) => {
    setLanguageOpen(false)
    // Persist only a real change — the shown default is never written silently.
    if (picked.id !== lang.id) {
      track('txn_alert_language_changed', { from: lang.key, to: picked.key })
      dispatch(editEntity({ key: 'lang', entityId, entityType, value: picked }))
    }
  }

  const onBuyPlan = () => {
    track('txn_alert_buy_plan_clicked', { whatsapp_offered: whatsappOffered })
    setShowUpgrade(false)
    // Switch this account on once the plan is paid for, then land on My Plan with the
    // "Transaction alert from OkCredit" channel picker open (KMP SavePendingAlertUpgrade +
    // openVariantPickerOnLoad).
    dispatch(setPendingTxnAlertUpgrade({ entityId, entityType, awaited: 'okc_alerts' }))
    goToMyPlanPicker()
  }

  return (
    <div ref={rootRef} className={classNames(classes.root, className)}>
      {entity.isRegistered ? (
        <div className={classes.registeredStrip}>
          <BellIcon />
          <span>{t('dashboard:TxnAlertRegisteredInfo', { name })}</span>
        </div>
      ) : null}

      {/* Language card — always enabled, independent of premium and of the alert toggle */}
      <div className={classes.card}>
        <div
          className={classNames(classes.cardBody, classes.clickable)}
          role="button"
          tabIndex={0}
          aria-expanded={languageOpen}
          onClick={() => setLanguageOpen((open) => !open)}
          onKeyDown={(e) => e.key === 'Enter' && setLanguageOpen((open) => !open)}
        >
          <div className={classes.headerRow}>
            <div className={classes.titleCol}>
              <div className={classes.titleRow}>
                <LanguageSvg className={classes.leadIcon} style={ICON_TINT} />
                <span className={classes.title}>{t('dashboard:TxnAlertLanguage')}</span>
              </div>
              <div className={classes.subtitle}>{t('dashboard:TxnAlertLanguageSubtitle')}</div>
            </div>
            <div className={classes.valueRow}>
              <span className={classes.value}>{shortLanguageLabel(lang)}</span>
              <ArrowSvg
                className={classNames(classes.chevron, { [classes.chevronOpen]: languageOpen })}
                style={ICON_TINT}
              />
            </div>
          </div>
        </div>
        {languageOpen ? (
          <div
            className={classes.langList}
            role="listbox"
            aria-label={t('dashboard:SelectTheLanguage')}
          >
            {LANGUAGES.map((option) => {
              const selected = option.id === lang.id
              return (
                <div
                  key={option.id}
                  role="option"
                  aria-selected={selected}
                  className={classNames(classes.langItem, { [classes.langItemSelected]: selected })}
                  onClick={() => onPickLanguage(option)}
                >
                  <span>{option.label}</span>
                  {selected ? <TickSvg className={classes.langTick} /> : null}
                </div>
              )
            })}
          </div>
        ) : null}
      </div>

      {/* Transaction Alert card — the premium gate is on the toggle itself */}
      <div className={classes.card}>
        <div className={classes.cardBody}>
          <div className={classes.headerRow}>
            <div className={classes.titleRow}>
              <SmsSvg className={classes.leadIcon} style={ICON_TINT} />
              <span className={classes.title}>{t('dashboard:TxnAlertTitle')}</span>
            </div>
            <div className={classes.statusRow}>
              <span
                className={classNames(classes.statusText, { [classes.statusOn]: alertEnabled })}
              >
                {t(alertEnabled ? 'dashboard:StatusOn' : 'dashboard:StatusOff')}
              </span>
              <Switch
                checked={alertEnabled}
                onChange={onToggle}
                {...({ onColor: SWITCH_ON, offColor: SWITCH_OFF, height: 22, width: 44 } as any)}
              />
            </div>
          </div>
          <div className={classes.description}>
            {t('dashboard:TxnAlertDescription', { relation })}
          </div>
        </div>
        <div
          className={classNames(classes.phone, { [classes.phoneOff]: !alertEnabled })}
          data-theme-local="light"
          aria-hidden="true"
        >
          <div className={classes.phoneScreen}>
            <span
              className={classNames(classes.phoneNotch, { [classes.phoneNotchOff]: !alertEnabled })}
            />
            <div className={classNames(classes.bubble, { [classes.bubbleOff]: !alertEnabled })}>
              {sample}
            </div>
          </div>
        </div>
      </div>

      {channelSheetOpen ? (
        <TxnAlertChannelSheet
          anchor={sheetAnchor}
          selected={pickedChannel}
          onSelect={setPickedChannel}
          onProceed={onChannelProceed}
          onDismiss={() => setChannelSheetOpen(false)}
        />
      ) : null}
      {showUpgrade ? (
        <AnchoredSheet anchor={sheetAnchor} onDismiss={() => setShowUpgrade(false)}>
          <div className={classes.upgradeBody}>
            <div className={classes.sheetText}>
              {t(
                whatsappOffered
                  ? 'dashboard:UpgradePremiumWhatsappSmsAlert'
                  : 'dashboard:UpgradePremiumSmsAlert'
              )}
            </div>
            <div className={classes.sheetButtons}>
              <button
                type="button"
                className={classes.sheetSecondary}
                onClick={() => setShowUpgrade(false)}
              >
                {t('dashboard:Cancel')}
              </button>
              <button type="button" className={classes.sheetPrimary} onClick={onBuyPlan}>
                {/* iOS TwinBottomButtons: subscription badge icon before the label */}
                <CogIcon size={18} fill={THEME.onBrand} />
                {t('dashboard:BuyPlan')}
              </button>
            </div>
          </div>
        </AnchoredSheet>
      ) : null}
    </div>
  )
}

export default TransactionAlertSettings
